import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useRecaptcha from '../hooks/useRecaptcha';
import { getApiUrl } from '../config/api.config';
import './Contact.css';

const Contact = () => {
  const { t } = useTranslation();
  const { executeRecaptcha, isLoaded: recaptchaLoaded } = useRecaptcha();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(prevStatus => ({ ...prevStatus, submitting: true }));

    try {
      // Get reCAPTCHA token
      let recaptchaToken = null;
      if (recaptchaLoaded) {
        try {
          recaptchaToken = await executeRecaptcha('contact_form');
          console.log('reCAPTCHA token obtained');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA execution failed:', recaptchaError);
          // Continue without reCAPTCHA if it fails
        }
      }

      // Prepare form data with reCAPTCHA token
      const submissionData = {
        ...formData,
        recaptchaToken,
        timestamp: new Date().toISOString(),
      };

      // Send to Laravel API
      const response = await fetch(getApiUrl('/contact'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to send message');
      }

      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: result.message || t('contact.success') }
      });

      // Reset forme nakon 5 sekundi
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        setStatus({
          submitted: false,
          submitting: false,
          info: { error: false, msg: null }
        });
      }, 5000);

    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: t('contact.error') }
      });
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="container">
        <div className="section-header fade-in-up text-center">
          <h2>{t('contact.title')}</h2>
          <p className="contact-subtitle">{t('contact.subtitle')}</p>
        </div>

        <div className="contact-wrapper">
          {/* Google Map */}
          <div className="contact-map fade-in-up delay-100">
            <iframe
              title="Google Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2804.8754892825655!2d14.440945976532847!3d45.32693047107379!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4764ab1d8c8b7f7b%3A0x40b2f0f2f2f2f2f2!2sMedovi%C4%87eva%20ul.%203%2C%2051000%2C%20Rijeka!5e0!3m2!1sen!2shr!4v1234567890123!5m2!1sen!2shr"
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          {/* Contact Info & Form */}
          <div className="contact-content">
            {/* Contact Info */}
            <div className="contact-info fade-in-up delay-200">
              <h3>{t('contact.info.title')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">{t('contact.info.address')}</div>
                  <div className="info-value">{t('contact.info.addressValue')}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('contact.info.email')}</div>
                  <div className="info-value">
                    <a href="mailto:info@loga.hr">{t('contact.info.emailValue')}</a>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('contact.info.phone')}</div>
                  <div className="info-value">
                     <a href="tel:+385919796179">{t('contact.info.phoneValue')}</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="contact-form fade-in-up delay-300">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.name')}
                    required
                    disabled={status.submitting}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.email')}
                    required
                    disabled={status.submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('contact.form.phone')}
                    disabled={status.submitting}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('contact.form.company')}
                    disabled={status.submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <textarea
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.message')}
                  required
                  disabled={status.submitting}
                ></textarea>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={status.submitting}
              >
                {status.submitting ? t('contact.form.sending') : t('contact.form.send')}
              </button>

              {status.info.msg && (
                <div className={`form-message ${status.info.error ? 'error' : 'success'}`}>
                  {status.info.msg}
                </div>
              )}

              {/*<div className="recaptcha-notice">*/}
              {/*  This site is protected by reCAPTCHA and the Google{' '}*/}
              {/*  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}*/}
              {/*  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.*/}
              {/*</div>*/}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;